import {
  FireblocksNCWFactory,
  InMemorySecureStorageProvider,
  InMemoryStorageProvider,
} from "@fireblocks/ncw-js-sdk";
import axios from "axios";
import {
  PrivateKeyVariants,
  Secp256k1PrivateKey,
  PrivateKey,
} from "@aptos-labs/ts-sdk";
import { Buffer } from "buffer";
window.Buffer = Buffer;

// Configuration Constants
const BACKEND_SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;
const TX_UUID = process.env.REACT_APP_TX_UUID;

const PASSPHRASE = process.env.REACT_APP_PASSPHRASE;
const PASSPHRASE_ID = process.env.REACT_APP_PASSPHRASE_ID;


// Global
let fireblocksNCW; //Fireblocks SDK instance
let walletId;
let aptosAddress;

let deviceId;
let authProvider;
let partner;
let brandSource;
let accessToken;
let fireblocksEnvironment;
let aptosEnvironment;

let idmTxRef = "00000000-0000-0000-0000-000000000000";
let apiVersion = "2023.2";
let byPassVppa = true;
let clientId = "moviebonus_web";

export function setupSDK(payload) {
  deviceId = payload["deviceId"];
  fireblocksEnvironment = payload["fireblocksEnvironment"];
  aptosEnvironment = payload["aptosEnvironment"];
  authProvider = payload["authProvider"];
  partner = payload["partner"];
  brandSource = payload["brandSource"];
  accessToken = payload["accessToken"];

  idmTxRef = payload["idm_tx_ref"];
  apiVersion = payload["api-version"];
  byPassVppa = payload["x-idm-bypass-vppa"];
  clientId = payload["client_id"];

  console.log("Setup SDK completed");
  console.log(
    deviceId,
    fireblocksEnvironment,
    aptosEnvironment,
    authProvider,
    partner,
    brandSource,
    accessToken,
    idmTxRef,
    apiVersion,
    byPassVppa,
    clientId
  );

}

/**
 * Ensures the Fireblocks SDK is initialized.
 */
export function ensureSDKInitialized() {
  if (!fireblocksNCW) {
    throw new Error("SDK not initialized. Call initializeSDK() first.");
  }
}

/**
 * Fetches user information from the backend.
 */
export async function getUser() {
  console.log("Fetching user information...");
  try {
    const url = `${BACKEND_SERVER_URL}/user`;
    console.log(`Requesting URL: ${url}`);
    const response = await axios.get(url, {
      headers: {
        authProvider: authProvider,
        partner: partner,
        "x-idm-brand-source": brandSource,
        idm_tx_ref: idmTxRef,
        "api-version": apiVersion,
        "x-idm-bypass-vppa": byPassVppa,
        client_id: clientId,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    walletId = response.data.walletId;
    aptosAddress = response.data.address;

    console.log(
      "User information retrieved successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user information:", error);
    throw error;
  }
}

/**
 * Fetches user assets from the backend.
 */
export async function getUserAssets() {
  console.log("Fetching user assets...");
  try {
    const url = `${BACKEND_SERVER_URL}/user/assets`;
    console.log(`Requesting URL: ${url}`);
    const response = await axios.get(url, {
      headers: {
        authProvider: authProvider,
        partner: partner,
        "x-idm-brand-source": brandSource,
        idm_tx_ref: idmTxRef,
        "api-version": apiVersion,
        "x-idm-bypass-vppa": byPassVppa,
        client_id: clientId,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const assets = response.data.results;

    console.log(
      "User asset information retrieved successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return assets;
  } catch (error) {
    console.error("Error fetching user information:", error);
    throw error;
  }
}

/**
 * Posts an action complete for a user.
 */

export async function postUserAction(rewardId){
  try {
    const response = await axios.post(
      `${BACKEND_SERVER_URL}/user/action/complete`,
      {
        universalId: rewardId,
      },
      {
        headers: {
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    console.log(
      "User action updated successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return {
      success: true, 
      data: response.data
    };
  } catch (error) {
    console.error("Error fetching user information:", error);
    throw error;
  }
  }

/**
 * Gets rewards for a user
 */

export async function getUserRewards(){
  try {
    const response = await axios.get(
      `${BACKEND_SERVER_URL}/user/reward?status=UNCLAIMED`,
      {
        headers: {
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    console.log(
      "User rewards retrieved successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return {
      success: true, 
      data: response.data
    };
  } catch (error) {
    console.error("Error fetching user information:", error);
    throw error;
  }
  }

  /**
 * Starts a claim for a user.
 */

export async function postUserClaim(rewardId){
  try {
    const response = await axios.post(
      `${BACKEND_SERVER_URL}/user/reward/${rewardId}/claim`,
      {},
      {
        headers: {
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    console.log(
      "User claimed successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return {
      success: true, 
      data: response.data
    };
  } catch (error) {
    console.error("Error claiming reward for user:", error);
    throw error;
  }
  }

/**
 * Register a new device.
 */
export async function register() {
  console.log("Registering new device...", deviceId, accessToken);

  try {
    const response = await axios.post(
      `${BACKEND_SERVER_URL}/user/register`,
      {
        deviceId: deviceId,
      },
      {
        headers: {
          authProvider: authProvider,
          partner: partner,
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    walletId = response.data.walletId;
    aptosAddress = response.data.address;

    console.log(
      "Registered new device successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user information:", error);
    throw error;
  }
}

/**
 * Initializes the Fireblocks SDK.
 */
export async function initializeSDK() {
  console.log("Initializing Fireblocks NCW SDK...");

  const secureStorageProvider = new InMemorySecureStorageProvider();
  const storageProvider = new InMemoryStorageProvider();

  const messagesHandler = {
    handleOutgoingMessage: async (message) => {
      console.log("Outgoing message to backend:", message);
      try {
        const response = await axios.post(
          `${BACKEND_SERVER_URL}/fireblocks/${deviceId}/rpc`,
          { message },
          {
            headers: {
              authProvider: authProvider,
              partner: partner,
              "x-idm-brand-source": brandSource,
              idm_tx_ref: idmTxRef,
              "api-version": apiVersion,
              "x-idm-bypass-vppa": byPassVppa,
              client_id: clientId,
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        console.log(
          "Backend response received:",
          JSON.stringify(response.data, null, 2)
        );
        return response.data;
      } catch (error) {
        console.error("Error communicating with backend:", error);
        throw error;
      }
    },
  };

  const eventsHandler = {
    handleEvent: (event) => {
      console.log("Event received:", JSON.stringify(event, null, 2));
    },
  };

  try {
    fireblocksNCW = await FireblocksNCWFactory({
      env: fireblocksEnvironment,
      deviceId: deviceId,
      messagesHandler,
      eventsHandler,
      secureStorageProvider,
      storageProvider,
    });
    console.log("Fireblocks NCW SDK initialized successfully.");
    return true;
  } catch (error) {
    console.error("Error initializing Fireblocks NCW SDK:", error);
    throw error;
  }
}

/**
 * Generates MPC Keys.
 */
export async function generateMPCKeys() {
  ensureSDKInitialized();

  console.log("Generating MPC Keys...");
  try {
    const algorithms = new Set(["MPC_CMP_ECDSA_SECP256K1"]);
    const keyDescriptors = await fireblocksNCW.generateMPCKeys(algorithms);
    console.log(
      "MPC Keys generated successfully:",
      JSON.stringify(keyDescriptors, null, 2)
    );
    return keyDescriptors;
  } catch (error) {
    console.error("Error generating MPC Keys:", error);
    throw error;
  }
}

/**
 * Checks the status of MPC Keys.
 */
export async function checkKeyStatus() {
  ensureSDKInitialized();

  console.log("Checking MPC Keys status...");
  try {
    const keysStatus = await fireblocksNCW.getKeysStatus();
    console.log("MPC Keys status:", JSON.stringify(keysStatus, null, 2));
    return keysStatus;
  } catch (error) {
    console.error("Error checking MPC Keys status:", error);
    throw error;
  }
}

/**
 * Backs up MPC Keys with the given passphrase and passphrase ID.
 */
export async function backupKeys() {
  ensureSDKInitialized();

  console.log("Backing up MPC Keys...");

  // Backup the passphrase id in the backend
  await backupPassphrase(PASSPHRASE_ID, "LocalStorage");

  try {
    await fireblocksNCW.backupKeys(PASSPHRASE, PASSPHRASE_ID);
    console.log("MPC Keys backed up successfully.");
  } catch (error) {
    console.error("Error backing up MPC Keys:", error);
    throw error;
  }
}

/**
 * Backs up a passphrase and passphrase ID to the backend.
 * @param {string} passphraseId - The ID of the passphrase to backup.
 * @param {string} location - The location where the passphrase is stored.
 * https://ncw-developers.fireblocks.com/docs/key-handler
 * https://ncw-developers.fireblocks.com/docs/backup-recovery-1
 */
export async function backupPassphrase(passphraseId, location) {
  console.log("Backing up passphrase on backend...");

  try {
    const response = await axios.post(
      `${BACKEND_SERVER_URL}/fireblocks/passphrase`,
      {
        passphraseId,
        location, // e.g. "Google Drive","iCloud","Local"
      },
      {
        headers: {
          authProvider: authProvider,
          partner: partner,
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    console.log(
      "Passphrase backed up successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return response.data;
  } catch (error) {
    console.error("Error backing up passphrase:", error);
    throw error;
  }
}

/**
 * Recovers MPC Keys using a passphrase resolver.
 * https://ncw-developers.fireblocks.com/docs/backup-recovery-1
 */
export async function recoverKeys() {
  ensureSDKInitialized();

  console.log("Recovering MPC Keys...");
  try {
    // Fetch the passphrase ID and location from the backend
    await getPassphrase();

    const passphraseResolver = async (passphraseId) => PASSPHRASE;

    await fireblocksNCW.recoverKeys(passphraseResolver);

    console.log("MPC Keys recovered successfully.");

    return true;
  } catch (error) {
    console.error("Error recovering MPC Keys:", error);
    return false;
  }
}

/**
 * Retrieves the passphrase and passphrase ID from the backend.
 */
export async function getPassphrase() {
  console.log("Fetching passphrase information from backend...");

  try {
    const response = await axios.get(
      `${BACKEND_SERVER_URL}/fireblocks/passphrase`,
      {
        headers: {
          authProvider: authProvider,
          partner: partner,
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    console.log(
      "Passphrase information retrieved successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching passphrase information:", error);
    throw error;
  }
}

/**
 * Generates a new address for the wallet.
 */
export async function generateWalletAddress() {
  console.log("Generating wallet address...");

  try {
    const response = await axios.post(
      `${BACKEND_SERVER_URL}/fireblocks/address`,
      {}, // No body required for this request
      {
        headers: {
          authProvider: authProvider,
          partner: partner,
          "x-idm-brand-source": brandSource,
          idm_tx_ref: idmTxRef,
          "api-version": apiVersion,
          "x-idm-bypass-vppa": byPassVppa,
          client_id: clientId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    aptosAddress = response.data.address;
    console.log(
      "Wallet address generated successfully:",
      JSON.stringify(response.data, null, 2)
    );
    return response.data;
  } catch (error) {
    console.error("Error generating wallet address:", error);
    throw error;
  }
}

/**
 * Requests to join an existing wallet.
 * Emits a `requestId` when the request is created and logs it for later approval.
 */
export async function requestJoinExistingWallet() {
  ensureSDKInitialized(); // Ensure the SDK is initialized

  console.log("Requesting to join an existing wallet...");

  try {
    await fireblocksNCW.requestJoinExistingWallet({
      onRequestId(requestId) {
        console.log("Join wallet request ID received:", requestId);

        // Store the requestId for later approval
        window.lastRequestId = requestId;

        console.log(
          `You can approve this request manually using approveJoinWalletRequest('${requestId}').`
        );
      },
    });

    console.log("Join wallet request sent successfully.");
  } catch (error) {
    console.error("Error requesting to join wallet:", error);
    throw error;
  }
}

/**
 * Approves a join wallet request using the requestId.
 * @param {string} requestId - The ID of the join wallet request to approve.
 */
export async function approveJoinWalletRequest(requestId) {
  ensureSDKInitialized(); // Ensure the SDK is initialized
  if (!requestId) {
    requestId = prompt("Insert encoded request data");
  }
  console.log(`Approving join wallet request with ID: ${requestId}...`);

  try {
    const result = await fireblocksNCW.approveJoinWalletRequest(requestId);
    console.log("Join wallet request approved successfully:", result);
    return result;
  } catch (error) {
    console.error("Error approving join wallet request:", error);
    throw error;
  }
}

/**
 * Signs a transaction using the given transaction UUID.
 */
export async function signTransaction() {
  ensureSDKInitialized();

  console.log(`Signing transaction with UUID: ${TX_UUID}...`);
  try {
    const result = await fireblocksNCW.signTransaction(TX_UUID);
    console.log("Transaction signature result:", {
      txId: result.txId,
      status: result.transactionSignatureStatus,
    });
    return result;
  } catch (error) {
    console.error("Error signing transaction:", error);
    throw error;
  }
}

/**
 * Retrieves assets from the backend.
 */
export async function getAssets() {
  console.log("Retrieving assets...");

  try {
    const response = await axios.get(`${BACKEND_SERVER_URL}/user/assets`, {
      headers: {
        authProvider: authProvider,
        partner: partner,
        "x-idm-brand-source": brandSource,
        idm_tx_ref: idmTxRef,
        "api-version": apiVersion,
        "x-idm-bypass-vppa": byPassVppa,
        client_id: clientId,
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const assets = response.data;
    console.log("Retrieved assets:", JSON.stringify(assets, null, 2));

    return assets;
  } catch (error) {
    console.error("Error retrieving assets:", error);
    throw error;
  }
}

/**
 * Automatically run a full test sequence on page load (optional).
 */
async function runFullTestSequence() {
  console.log("Running full test sequence...");
  try {
    await register();
    await initializeSDK();
    await generateMPCKeys();
    await checkKeyStatus();
    await backupKeys();
    await generateWalletAddress();
    console.log("Full test sequence completed successfully.");
  } catch (error) {
    console.error("Error during full test sequence:", error);
  }
}

/**
 * Export private key
 */
export async function exportPrivateKey() {
  console.log("Running exportPrivateKey method...");
  try {
    if (!fireblocksNCW) {
      throw new Error("fireblocksNCW is not initialized");
    }

    const fullKey = await fireblocksNCW.takeover();
    const coin_symbol = 637;
    const accountId = 0;
    const change = 0;
    const index = 0;

    const privateKeyHexStr = await fireblocksNCW.deriveAssetKey(
      fullKey[0].privateKey,
      coin_symbol,
      accountId,
      change,
      index
    );

    const privateKeyFormatted = PrivateKey.formatPrivateKey(
      privateKeyHexStr,
      PrivateKeyVariants.Secp256k1
    );
    const privateKeyObj = new Secp256k1PrivateKey(privateKeyFormatted);
    const pk = `secp256k1-priv-${privateKeyObj.toHexString()}`;
    console.log("Private key", pk);

    return pk;
  } catch (error) {
    console.error("Error during exportPrivateKey method:", error);
  }
}

// Expose functions globally for browser console access
window.getUser = getUser;
window.register = register;
window.setupSDK = setupSDK;
window.initializeSDK = initializeSDK;
window.generateMPCKeys = generateMPCKeys;
window.checkKeyStatus = checkKeyStatus;
window.backupKeys = backupKeys;
window.recoverKeys = recoverKeys;
window.getPassphrase = getPassphrase;
window.generateWalletAddress = generateWalletAddress;
window.requestJoinExistingWallet = requestJoinExistingWallet;
window.approveJoinWalletRequest = approveJoinWalletRequest;
window.signTransaction = signTransaction;
window.runFullTestSequence = runFullTestSequence;
window.exportPrivateKey = exportPrivateKey;
window.getAssets = getAssets;
